import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../store/slices/authSlice';
import {
  deletePropertyStart,
  updatePropertyStart,
  createPropertyStart,
  fetchPropertiesStart,
} from '../store/slices/propertiesSlice';
import { useTranslation } from '../utils/translations';
import { formatPrice, isPriceOnRequest } from '../utils/propertyHelpers';
import { selectAllProperties, selectPropertiesLoading } from '../store/selectors/propertySelectors';
import { LISTING_STATUS, resolveListingStatus, getListingStatusLabel } from '../constants/listingStatus';
import { agentsAPI } from '../services/api';
import { getPropertiesForAgentUser, propertyBelongsToAgentUser } from '../utils/agentHelpers';
import PropertyFormModal from '../components/PropertyFormModal/PropertyFormModal';
import AgentFormModal from '../components/AgentFormModal/AgentFormModal';
import AgentRegisterModal from '../components/AgentRegisterModal/AgentRegisterModal';
import ChangePasswordModal from '../components/ChangePasswordModal/ChangePasswordModal';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { SkeletonTable } from '../components/loading';
import './AdminDashboardPage.css';

const getFirstPropertyImage = (property) => {
  const images = property?.images || [];
  return images.find((image) => image && String(image).trim()) || null;
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const properties = useSelector(selectAllProperties);
  const propertiesLoading = useSelector(selectPropertiesLoading);
  const { theme } = useSelector((state) => state.ui);
  const isAdmin = user?.username === 'admin';

  const visibleProperties = useMemo(
    () => getPropertiesForAgentUser(properties, user),
    [properties, user]
  );

  const [activeSection, setActiveSection] = useState('properties');
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isAgentRegisterModalOpen, setIsAgentRegisterModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentSortField, setAgentSortField] = useState('name');
  const [agentSortDirection, setAgentSortDirection] = useState('asc');

  const sortedProperties = useMemo(() => {
    const sorted = [...visibleProperties];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'location':
          comparison = (a.location || '').localeCompare(b.location || '', undefined, { sensitivity: 'base' });
          break;
        case 'price':
          if (isPriceOnRequest(a.currency) && isPriceOnRequest(b.currency)) {
            comparison = 0;
          } else if (isPriceOnRequest(a.currency)) {
            comparison = 1;
          } else if (isPriceOnRequest(b.currency)) {
            comparison = -1;
          } else {
            comparison = Number(a.price) - Number(b.price);
          }
          break;
        case 'type':
          comparison = (a.propertyType || '').localeCompare(b.propertyType || '', undefined, { sensitivity: 'base' });
          break;
        case 'id':
        default:
          comparison = Number(a.id) - Number(b.id);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [visibleProperties, sortField, sortDirection]);

  const sortedAgents = useMemo(() => {
    const sorted = [...agents];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (agentSortField) {
        case 'city':
          comparison = (a.city || '').localeCompare(b.city || '', undefined, { sensitivity: 'base' });
          break;
        case 'propertiesCount':
          comparison = Number(a.propertiesCount || 0) - Number(b.propertiesCount || 0);
          break;
        case 'name':
        default:
          comparison = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
          break;
      }

      return agentSortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [agents, agentSortField, agentSortDirection]);

  const loadAgents = useCallback(async () => {
    if (!isAdmin) return;

    try {
      setAgentsLoading(true);
      let data;
      try {
        data = await agentsAPI.getAllForManage();
      } catch (manageError) {
        console.warn('Manage agents endpoint unavailable, using public agents list:', manageError);
        data = await agentsAPI.getAll();
      }
      setAgents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    } else {
      dispatch(fetchPropertiesStart());
    }
  }, [isAuthenticated, navigate, dispatch]);

  useEffect(() => {
    if (isAuthenticated && isAdmin && activeSection === 'agents') {
      loadAgents();
    }
  }, [isAuthenticated, isAdmin, activeSection, loadAgents]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection(field === 'id' ? 'desc' : 'asc');
  };

  const handleAgentSort = (field) => {
    if (agentSortField === field) {
      setAgentSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setAgentSortField(field);
    setAgentSortDirection('asc');
  };

  const getSortIndicator = (field, currentField, direction) => {
    if (currentField !== field) return '';
    return direction === 'asc' ? ' ↑' : ' ↓';
  };

  const getSortAriaLabel = (field, label, currentField, direction) => {
    if (currentField !== field) {
      return `${label}, ${t('admin.dashboard.sortable')}`;
    }

    return direction === 'asc'
      ? `${label}, ${t('admin.dashboard.sortedAscending')}`
      : `${label}, ${t('admin.dashboard.sortedDescending')}`;
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/admin/login');
  };

  const handleAddProperty = () => {
    setEditingProperty(null);
    setIsPropertyModalOpen(true);
  };

  const handleEditProperty = (property) => {
    if (!propertyBelongsToAgentUser(user, property)) {
      alert(t('admin.form.saveError') || 'You do not have permission to edit this property');
      return;
    }
    setEditingProperty(property);
    setIsPropertyModalOpen(true);
  };

  const handleDeleteProperty = (propertyId) => {
    const property = visibleProperties.find((item) => item.id === propertyId);
    if (property && !propertyBelongsToAgentUser(user, property)) {
      alert(t('admin.form.saveError') || 'You do not have permission to delete this property');
      return;
    }

    if (window.confirm(t('admin.form.confirmDelete') || t('confirm Delete'))) {
      dispatch(deletePropertyStart(propertyId));
    }
  };

  const handleSaveProperty = async (propertyData) => {
    try {
      const dataWithAgent = { ...propertyData };

      if (!isAdmin) {
        if (editingProperty && !propertyBelongsToAgentUser(user, editingProperty)) {
          alert(t('admin.form.saveError') || 'You do not have permission to edit this property');
          return;
        }
        dataWithAgent.agentId = editingProperty?.agentId || user?.agentId || dataWithAgent.agentId;
      }

      if (editingProperty) {
        dispatch(updatePropertyStart({ ...dataWithAgent, id: editingProperty.id }));
      } else {
        dispatch(createPropertyStart(dataWithAgent));
      }
      setIsPropertyModalOpen(false);
      setEditingProperty(null);
    } catch (error) {
      console.error('Error saving property:', error);
      alert(t('admin.form.saveError') || t('aveError') || 'Failed to save property');
    }
  };

  const handleAddAgent = () => {
    setIsAgentRegisterModalOpen(true);
  };

  const handleEditAgent = async (agent) => {
    try {
      let agentData = agent;
      try {
        agentData = await agentsAPI.getByIdForManage(agent.id);
      } catch (manageError) {
        console.warn('Could not load full agent details, using list data:', manageError);
      }
      setEditingAgent(agentData);
      setIsAgentModalOpen(true);
    } catch (error) {
      alert(error.message || t('admin.agentForm.saveError') || 'Failed to load agent');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm(t('admin.agentForm.confirmDelete') || 'Are you sure you want to delete this agent?')) {
      return;
    }

    try {
      await agentsAPI.delete(agentId);
      await loadAgents();
    } catch (error) {
      alert(error.message || t('admin.agentForm.deleteError') || 'Failed to delete agent');
    }
  };

  const handleSaveAgent = async (agentData) => {
    if (!editingAgent) return;

    await agentsAPI.update(editingAgent.id, agentData);
    setIsAgentModalOpen(false);
    setEditingAgent(null);
    await loadAgents();
  };

  const handleAgentRegisterSuccess = () => {
    setIsAgentRegisterModalOpen(false);
    loadAgents();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={`admin-dashboard ${theme}`}>
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <div className="admin-header-title">
              <h1>{t('admin.dashboard.welcome')} {isAdmin ? 'Admin' : (user?.username || '')}</h1>
            </div>
            <div className="admin-header-actions">
              <div className="admin-section-tabs">
                <button
                  type="button"
                  className={`admin-section-tab ${activeSection === 'properties' ? 'active' : ''}`}
                  onClick={() => setActiveSection('properties')}
                >
                  {t('admin.dashboard.propertiesTab')}
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className={`admin-section-tab ${activeSection === 'agents' ? 'active' : ''}`}
                    onClick={() => setActiveSection('agents')}
                  >
                    {t('admin.dashboard.agentsTab')}
                  </button>
                )}
              </div>
              <button
                type="button"
                className="change-password-button"
                onClick={() => setIsChangePasswordModalOpen(true)}
              >
                {t('admin.dashboard.changePassword')}
              </button>
              <button onClick={handleLogout} className="logout-button">
                {t('admin.dashboard.logout') || t('Logout')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="container">
          {activeSection === 'properties' && (
            <>
              <div className="admin-stats">
                <div className="stat-card">
                  <h3>{t('admin.dashboard.totalProperties') || t('Total Properties')}</h3>
                  <p className="stat-number">{visibleProperties.length}</p>
                </div>
                <div className="stat-card">
                  <h3>{t('admin.dashboard.activeListings') || t('ActiveListings') || 'Active Listings'}</h3>
                  <p className="stat-number">
                    {visibleProperties.filter((p) => resolveListingStatus(p) === LISTING_STATUS.NONE).length}
                  </p>
                </div>
                <div className="stat-card">
                  <h3>{t('admin.dashboard.reservedProperties') || 'Rezervuar'}</h3>
                  <p className="stat-number">
                    {visibleProperties.filter((p) => resolveListingStatus(p) === LISTING_STATUS.RESERVED).length}
                  </p>
                </div>
                <div className="stat-card">
                  <h3>{t('admin.dashboard.soldProperties') || 'Shitur'}</h3>
                  <p className="stat-number">
                    {visibleProperties.filter((p) => resolveListingStatus(p) === LISTING_STATUS.SOLD).length}
                  </p>
                </div>
              </div>

              <div className="admin-section">
                <div className="section-header">
                  <h2>{t('admin.dashboard.propertiesManagement') || t('PropertiesManagement')}</h2>
                  <button onClick={handleAddProperty} className="add-property-button">
                    + {t('admin.dashboard.addProperty') || t('AddProperty')}
                  </button>
                </div>
                <div className="properties-table">
                  {propertiesLoading && properties.length === 0 ? (
                    <SkeletonTable rows={6} columns={5} withPhoto />
                  ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <button
                            type="button"
                            className="sortable-header"
                            onClick={() => handleSort('id')}
                            aria-label={getSortAriaLabel('id', t('admin.dashboard.id'), sortField, sortDirection)}
                          >
                            {t('admin.dashboard.id')}
                            <span className="sort-indicator">{getSortIndicator('id', sortField, sortDirection)}</span>
                          </button>
                        </th>
                        <th className="property-photo-column">{t('admin.dashboard.photo')}</th>
                        <th>
                          <button
                            type="button"
                            className="sortable-header"
                            onClick={() => handleSort('location')}
                            aria-label={getSortAriaLabel('location', t('admin.dashboard.location'), sortField, sortDirection)}
                          >
                            {t('admin.dashboard.location')}
                            <span className="sort-indicator">{getSortIndicator('location', sortField, sortDirection)}</span>
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className="sortable-header"
                            onClick={() => handleSort('price')}
                            aria-label={getSortAriaLabel('price', t('admin.dashboard.price'), sortField, sortDirection)}
                          >
                            {t('admin.dashboard.price')}
                            <span className="sort-indicator">{getSortIndicator('price', sortField, sortDirection)}</span>
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className="sortable-header"
                            onClick={() => handleSort('type')}
                            aria-label={getSortAriaLabel('type', t('admin.dashboard.type'), sortField, sortDirection)}
                          >
                            {t('admin.dashboard.type')}
                            <span className="sort-indicator">{getSortIndicator('type', sortField, sortDirection)}</span>
                          </button>
                        </th>
                        <th>{t('admin.form.listingStatus')}</th>
                        <th>{t('admin.dashboard.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedProperties.map((property) => {
                        const firstImage = getFirstPropertyImage(property);
                        const listingStatus = resolveListingStatus(property);
                        const listingStatusLabel = getListingStatusLabel(listingStatus, t);
                        return (
                        <tr key={property.id}>
                          <td>{property.id}</td>
                          <td className="property-photo-cell">
                            {firstImage ? (
                              <img
                                src={resolveMediaUrl(firstImage)}
                                alt={property.title || property.location || `Property ${property.id}`}
                                className="property-table-thumb"
                                loading="lazy"
                              />
                            ) : (
                              <span className="property-table-thumb-placeholder" aria-hidden="true">—</span>
                            )}
                          </td>
                          <td>{property.location}</td>
                          <td>
                            {property.transactionType === 'rent'
                              ? `${formatPrice(property.price, property.currency, t('propertyCard.priceOnRequest'))}${property.currency !== 'ON_REQUEST' ? `/${t('propertyCard.month')}` : ''}`
                              : formatPrice(property.price, property.currency, t('propertyCard.priceOnRequest'))}
                          </td>
                          <td>{t(`propertyTypes.${property.propertyType}`)}</td>
                          <td className="property-status-cell">
                            {listingStatusLabel ? (
                              <span className="badge badge-transaction">{listingStatusLabel}</span>
                            ) : null}
                          </td>
                          <td>
                            <button
                              className="action-button edit"
                              onClick={() => handleEditProperty(property)}
                            >
                              {t('admin.dashboard.edit') || t('Edit')}
                            </button>
                            <button
                              className="action-button delete"
                              onClick={() => handleDeleteProperty(property.id)}
                            >
                              {t('admin.dashboard.delete') || t('Delete')}
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  )}
                </div>
              </div>
            </>
          )}

          {activeSection === 'agents' && isAdmin && (
            <div className="admin-section">
              <div className="section-header">
                <h2>{t('admin.dashboard.agentsManagement')}</h2>
                <button onClick={handleAddAgent} className="add-property-button">
                  + {t('admin.dashboard.addAgent')}
                </button>
              </div>
              <div className="properties-table">
                {agentsLoading ? (
                  <SkeletonTable rows={5} columns={4} />
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <button
                            type="button"
                            className="sortable-header"
                            onClick={() => handleAgentSort('name')}
                            aria-label={getSortAriaLabel('name', t('admin.register.name'), agentSortField, agentSortDirection)}
                          >
                            {t('admin.register.name')}
                            <span className="sort-indicator">{getSortIndicator('name', agentSortField, agentSortDirection)}</span>
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className="sortable-header"
                            onClick={() => handleAgentSort('city')}
                            aria-label={getSortAriaLabel('city', t('admin.register.city'), agentSortField, agentSortDirection)}
                          >
                            {t('admin.register.city')}
                            <span className="sort-indicator">{getSortIndicator('city', agentSortField, agentSortDirection)}</span>
                          </button>
                        </th>
                        <th>{t('admin.register.email')}</th>
                        <th>{t('admin.register.mobile')}</th>
                        <th>
                          <button
                            type="button"
                            className="sortable-header"
                            onClick={() => handleAgentSort('propertiesCount')}
                            aria-label={getSortAriaLabel('propertiesCount', t('admin.dashboard.agentProperties'), agentSortField, agentSortDirection)}
                          >
                            {t('admin.dashboard.agentProperties')}
                            <span className="sort-indicator">{getSortIndicator('propertiesCount', agentSortField, agentSortDirection)}</span>
                          </button>
                        </th>
                        <th>{t('admin.dashboard.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAgents.map((agent) => (
                        <tr key={agent.id}>
                          <td>{agent.name}</td>
                          <td>{agent.city}</td>
                          <td>{agent.email}</td>
                          <td>{agent.mobile}</td>
                          <td>{agent.propertiesCount || 0}</td>
                          <td>
                            <button
                              className="action-button edit"
                              onClick={() => handleEditAgent(agent)}
                            >
                              {t('admin.dashboard.edit') || t('Edit')}
                            </button>
                            <button
                              className="action-button delete"
                              onClick={() => handleDeleteAgent(agent.id)}
                            >
                              {t('admin.dashboard.delete') || t('Delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PropertyFormModal
        isOpen={isPropertyModalOpen}
        onClose={() => {
          setIsPropertyModalOpen(false);
          setEditingProperty(null);
        }}
        property={editingProperty}
        onSave={handleSaveProperty}
        defaultAgentId={user?.username !== 'admin' ? (user?.agentId || '') : ''}
        lockAgentId={!isAdmin}
      />

      <AgentFormModal
        isOpen={isAgentModalOpen}
        onClose={() => {
          setIsAgentModalOpen(false);
          setEditingAgent(null);
        }}
        agent={editingAgent}
        onSave={handleSaveAgent}
      />

      <AgentRegisterModal
        isOpen={isAgentRegisterModalOpen}
        onClose={() => setIsAgentRegisterModalOpen(false)}
        onSuccess={handleAgentRegisterSuccess}
      />

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default AdminDashboardPage;
